/**
 * Export utilities for report pages: Excel and PDF.
 * Reports pass a title, file name, and table (headers + rows).
 */

export interface ReportExportTable {
  headers: string[];
  rows: (string | number)[][];
}

export interface ReportExportExcelExtraSheet {
  /** Worksheet tab name (keep short; Excel limits length). */
  name: string;
  /** Title row inside the sheet. */
  sheetTitle: string;
  table: ReportExportTable;
  /** 0-based column indices to right-align. */
  rightAlignColumnIndices?: number[];
}

export interface ReportExportPdfExtraSection {
  sectionTitle: string;
  table: ReportExportTable;
  rightAlignColumnIndices?: number[];
}

export interface ReportExportOptions {
  reportTitle: string;
  fileName: string;
  /** Primary table (first Excel worksheet; first PDF table). */
  table: ReportExportTable;
  /** 0-based column indices to right-align (e.g. numeric/currency columns). */
  rightAlignColumnIndices?: number[];
  /** Additional Excel worksheets in the same workbook. */
  additionalExcelSheets?: ReportExportExcelExtraSheet[];
  /** Additional PDF tables, each on a new page after the first table. */
  additionalPdfSections?: ReportExportPdfExtraSection[];
}

function fillExcelWorksheet(
  worksheet: import('exceljs').Worksheet,
  sheetTitle: string,
  table: ReportExportTable,
  rightAlignColumnIndices: number[],
): void {
  const colCount = Math.max(table.headers.length, ...table.rows.map((r) => r.length));
  const rightSet = new Set(rightAlignColumnIndices);

  const titleRow = worksheet.addRow([sheetTitle, ...Array(colCount - 1).fill('')]);
  worksheet.mergeCells(1, 1, 1, colCount);
  titleRow.getCell(1).font = { size: 14, bold: true };

  const dateRow = worksheet.addRow([`Generated: ${new Date().toLocaleString()}`, ...Array(colCount - 1).fill('')]);
  worksheet.mergeCells(2, 1, 2, colCount);
  dateRow.getCell(1).font = { size: 10 };

  worksheet.addRow([]);

  const headerRow = worksheet.addRow(table.headers);
  headerRow.eachCell((cell: any, colNumber: number) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
    cell.alignment = {
      horizontal: rightSet.has(colNumber - 1) ? 'right' : 'center',
      vertical: 'middle',
    };
  });

  table.rows.forEach((row, index) => {
    const excelRow = worksheet.addRow(row);
    excelRow.eachCell((cell: any, colNumber: number) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: index % 2 === 0 ? 'FFFFFFFF' : 'FFF5F5F5' },
      };
      if (rightSet.has(colNumber - 1)) {
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
      }
    });
  });

  worksheet.columns = table.headers.map((h, i) => ({
    width: Math.max(12, String(h).length + 2, ...table.rows.map((r) => String(r[i] || '').length + 2)),
  }));
}

/**
 * Export report table to Excel (.xlsx).
 */
export async function exportReportToExcel(options: ReportExportOptions): Promise<void> {
  const { reportTitle, fileName, table, rightAlignColumnIndices = [], additionalExcelSheets = [] } = options;
  const ExcelJS = await import('exceljs');
  const { saveAs } = await import('file-saver');

  const workbook = new (ExcelJS.default).Workbook();
  const firstSheetName = additionalExcelSheets.length > 0 ? 'By category' : 'Report';
  const first = workbook.addWorksheet(firstSheetName);
  fillExcelWorksheet(first, reportTitle, table, rightAlignColumnIndices);

  for (const sheet of additionalExcelSheets) {
    const ws = workbook.addWorksheet(sheet.name.slice(0, 31));
    fillExcelWorksheet(ws, sheet.sheetTitle, sheet.table, sheet.rightAlignColumnIndices ?? []);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const saveAsFn = (saveAs as any).default || saveAs;
  saveAsFn(blob, `${fileName}.xlsx`);
}

/**
 * Export report table to PDF.
 */
function buildPdfColumnStyles(indices: number[]): Record<number, { halign: 'right' }> {
  const columnStyles: Record<number, { halign: 'right' }> = {};
  indices.forEach((colIndex) => {
    columnStyles[colIndex] = { halign: 'right' };
  });
  return columnStyles;
}

export async function exportReportToPDF(options: ReportExportOptions): Promise<void> {
  const { reportTitle, fileName, table, rightAlignColumnIndices = [], additionalPdfSections = [] } = options;
  const jsPDF = (await import('jspdf')).default;
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(reportTitle, 14, 12);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const dateTime = new Date().toLocaleString();
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.text(dateTime, pageWidth - doc.getTextWidth(dateTime) - 14, 12);

  let columnStyles = buildPdfColumnStyles(rightAlignColumnIndices);

  autoTable(doc, {
    head: [table.headers],
    body: table.rows.map((row) => row.map((c) => String(c))),
    startY: 18,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: 14, right: 14 },
    columnStyles: Object.keys(columnStyles).length > 0 ? columnStyles : undefined,
  });

  for (const section of additionalPdfSections) {
    doc.addPage();
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(section.sectionTitle, 14, 12);
    doc.setFont('helvetica', 'normal');
    columnStyles = buildPdfColumnStyles(section.rightAlignColumnIndices ?? []);
    autoTable(doc, {
      head: [section.table.headers],
      body: section.table.rows.map((row) => row.map((c) => String(c))),
      startY: 18,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
      columnStyles: Object.keys(columnStyles).length > 0 ? columnStyles : undefined,
    });
  }

  doc.save(`${fileName}.pdf`);
}
